/**
 * Server-backed "This is me" link between an account owner and a person profile.
 *
 * Persists to the backend so the link works across devices and browsers.
 * Also mirrors to localStorage for fast local reads (same key as the legacy
 * myArchivePerson.js util so existing consumers still work).
 */
import axios from 'axios';
import { getApiBase } from './apiBase';
import { MY_ARCHIVE_PERSON_KEY } from './myArchivePerson';

const API = getApiBase();

/** LocalStorage key for dismissal state of the self-person onboarding prompt. */
export const SELF_PROMPT_DISMISSED_KEY = 'gb_self_prompt_dismissed';

/**
 * Set the self-person link on the server AND in localStorage.
 * @param {string} personId
 * @returns {Promise<void>}
 */
export async function setSelfPersonOnServer(personId) {
  await axios.patch(`${API}/users/me/self-person`, { person_id: personId });
  try {
    localStorage.setItem(MY_ARCHIVE_PERSON_KEY, String(personId));
    window.dispatchEvent(new Event('gb-my-archive-person-updated'));
  } catch {
    /* ignore */
  }
}

/**
 * Clear the self-person link on the server AND in localStorage.
 * @returns {Promise<void>}
 */
export async function clearSelfPersonOnServer() {
  await axios.patch(`${API}/users/me/self-person`, { person_id: null });
  try {
    localStorage.removeItem(MY_ARCHIVE_PERSON_KEY);
    window.dispatchEvent(new Event('gb-my-archive-person-updated'));
  } catch {
    /* ignore */
  }
}

/** Dismiss the self-person onboarding prompt. reason: 'other' | 'skip' */
export function dismissSelfPrompt(reason = 'skip') {
  try {
    localStorage.setItem(SELF_PROMPT_DISMISSED_KEY, reason);
  } catch {
    /* ignore */
  }
}

/** Returns the stored dismissal reason, or null if not dismissed. */
export function getSelfPromptDismissed() {
  try {
    return localStorage.getItem(SELF_PROMPT_DISMISSED_KEY) || null;
  } catch {
    return null;
  }
}
